import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Body from '../../ui/Body/body';
import i18n from '../../utils/i18n';
import { useFetchGet } from '../../utils/fetch/useFetchGet';
import { ICategory } from '../../utils/types';
import { paths, pathsApi } from '../../router/paths';
import s from '../Product/styles/product.module.scss';
import GridRow from '../../ui/GridRow';
import GridCol from '../../ui/GridCol';
import ButtonAnchor from '../../ui/Button/buttonAnchor';
import { getCatalogLink, getCategoryImgSrc, getHeroImgSrc, HOST_TKO } from '../../utils/url';
import { ButtonVariationEnum } from '../../ui/Button/types';
import Button from '../../ui/Button';
import TextItem from '../../ui/TextItem/textItem';
import Tag from '../../ui/Tag';
import { TagEnum } from '../../ui/Tag/types';
import { boolToReadable } from '../../utils/boolUtil';
import Card from '../../ui/Card';
import Link from '../../ui/Link';
import { IconEnum } from '../../ui/Icon';
import Details from '../../ui/Details/details';
import { GridAlignEnum } from '../../ui/GridRow/GridRow';
import { parseCategoryButton } from './utils/parseCategoryButton';
import SortProduct from './module/sortProduct';
import SortChildrenCategories from '../CategoryAll/modules/sortChildrenCategories';

const Category = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { data } = useFetchGet<ICategory>({
        path: pathsApi.category + id,
    });

    const editCategory = () => {
        navigate(paths.category + id + '/edit', { state: { category: data } });
    };

    if (!data) return <Body title={i18n.general.loading} isLoading={true} />;

    return (
        <Body title={data.name}>
            <GridRow gutter={16} className={s.root}>
                <GridCol col={12} className={s.buttonBlock}>
                    <ButtonAnchor
                        href={HOST_TKO + getCatalogLink(data.link)}
                        target={'_blank'}
                        variation={ButtonVariationEnum.SOFT}
                    >
                        {i18n.category.openCategory}
                    </ButtonAnchor>
                    <Button onClick={editCategory} variation={ButtonVariationEnum.SOFT}>
                        {i18n.general.edit}
                    </Button>
                    <SortChildrenCategories categoryId={data.id} />
                    <SortProduct categoryId={data.id} />
                </GridCol>
                <GridCol col={8}>
                    <GridRow gutter={16}>
                        <GridCol col={6}>
                            <TextItem label={i18n.general.name} text={data.name} />
                        </GridCol>
                        <GridCol col={6}>
                            <TextItem label={i18n.category.parentCategory}>
                                {data.parent ? (
                                    <Link to={paths.category + data.parent.id}>{data.parent?.name}</Link>
                                ) : (
                                    '-'
                                )}
                            </TextItem>
                        </GridCol>
                        <GridCol col={6}>
                            <TextItem label={i18n.product.isVisible}>
                                <Tag type={!!data.deletedAt ? TagEnum.RED : TagEnum.GREEN}>
                                    {boolToReadable(!data.deletedAt)}
                                </Tag>
                            </TextItem>
                        </GridCol>
                    </GridRow>
                </GridCol>
                <GridCol col={4} className={s.sideBlock}>
                    <Card>
                        <TextItem label={i18n.general.updated} text={data.updatedAt} />
                    </Card>
                </GridCol>
                <GridCol col={12}>
                    <Details label={i18n.general.images} icon={IconEnum.IMAGE}>
                        <GridRow alignRow={GridAlignEnum.TOP} gutter={8}>
                            <GridCol col={6}>
                                <Card>
                                    <GridRow gutter={8} alignRow={GridAlignEnum.TOP}>
                                        <GridCol col={12}>
                                            <b>{i18n.category.mainImage}</b>
                                        </GridCol>
                                        <GridCol col={6}>
                                            <img src={getCategoryImgSrc(data.img[0])} />
                                        </GridCol>
                                        <GridCol col={6}>
                                            <GridRow column gutter={8}>
                                                <TextItem label={i18n.images.alt} text={data.img[1]} />
                                                <TextItem label={i18n.images.title} text={data.img[2]} />
                                            </GridRow>
                                        </GridCol>
                                    </GridRow>
                                </Card>
                            </GridCol>
                            {data.hero && (
                                <GridCol col={6}>
                                    <Card>
                                        <GridRow gutter={8} alignRow={GridAlignEnum.TOP}>
                                            <GridCol col={12}>
                                                <b>{i18n.category.heroImage}</b>
                                            </GridCol>
                                            <GridCol col={6}>
                                                <img src={getHeroImgSrc(data.hero[0])} />
                                            </GridCol>
                                            <GridCol col={6}>
                                                <GridRow column gutter={8}>
                                                    <TextItem label={i18n.images.alt} text={data.hero[1]} />
                                                    <TextItem label={i18n.images.title} text={data.hero[2]} />
                                                </GridRow>
                                            </GridCol>
                                        </GridRow>
                                    </Card>
                                </GridCol>
                            )}
                        </GridRow>
                    </Details>
                    <Details label={i18n.category.description} icon={IconEnum.EDIT}>
                        <Card>
                            <p>{data.description}</p>
                        </Card>
                    </Details>
                    <Details label={i18n.general.filter} icon={IconEnum.FILTER}>
                        <GridRow isFlex gutter={8}>
                            {data.filtersGroup?.map((item) => <Tag key={item.id}>{item.name}</Tag>)}
                        </GridRow>
                    </Details>
                    <Details label={i18n.category.additionalButtons} icon={IconEnum.CATALOG}>
                        <GridRow gutter={8}>
                            <GridCol col={12}>
                                <p>{i18n.category.categoryExtraButtonsDescription}</p>
                            </GridCol>
                            {parseCategoryButton(data.buttons)?.map((item) => {
                                return (
                                    <GridCol key={item.name} col={6}>
                                        <Card>
                                            <GridRow gutter={8} alignRow={GridAlignEnum.TOP}>
                                                <GridCol col={12}>
                                                    <TextItem label={i18n.general.title} text={item.name} />
                                                </GridCol>
                                                <GridCol col={12}>
                                                    <TextItem label={i18n.general.link}>
                                                        <Link to={HOST_TKO + item.link} target={'_blank'}>
                                                            {item.link}
                                                        </Link>
                                                    </TextItem>
                                                </GridCol>
                                                <GridCol col={6}>
                                                    <img src={getCategoryImgSrc(item.img.src)} alt={item.img.alt} />
                                                </GridCol>
                                                <GridCol col={6}>
                                                    <GridRow column gutter={8}>
                                                        <TextItem label={i18n.images.alt} text={item.img.alt} />
                                                        <TextItem label={i18n.images.title} text={item.img.title} />
                                                    </GridRow>
                                                </GridCol>
                                            </GridRow>
                                        </Card>
                                    </GridCol>
                                );
                            })}
                        </GridRow>
                    </Details>
                    <Details label={i18n.seo.name} icon={IconEnum.SEARCH}>
                        <GridRow column gutter={12}>
                            <TextItem label={i18n.seo.metaTitle} text={data.metaTitle} />
                            <TextItem label={i18n.seo.metaDescription} text={data.metaDescription} />
                            <TextItem label={i18n.seo.keywords} text={data.keywords} />
                        </GridRow>
                    </Details>
                </GridCol>
            </GridRow>
        </Body>
    );
};

export default Category;
